class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    const sectionRequest = fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          const shouldUpdate = section.block === "cart-items" || updateFooter;
          if (!shouldUpdate) return;

          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;

          sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
        });
      })
      .catch((e) => {
        console.error(e);
        throw e;
      });

    const cartRequest = this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
      return cart;
    });

    return Promise.all([sectionRequest, cartRequest]).then(([, cart]) => cart);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-ecomhard-packaging]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-ecomhard-packaging]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);

if (!customElements.get("ecomhard-packaging-selector")) {
  class EcomhardPackagingSelector extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.checkbox = this.querySelector(".ecomhard-cart-packaging__checkbox");
      if (!this.checkbox || this.hasAttribute("data-initialized")) return;

      this.setAttribute("data-initialized", "true");
      this.checkbox.addEventListener("change", this.handleChange);
    }

    disconnectedCallback() {
      if (this.checkbox) {
        this.checkbox.removeEventListener("change", this.handleChange);
      }
    }

    async handleChange(event) {
      if (this.isLoading) return;

      const shouldAdd = event.currentTarget.checked;
      const variantId = Number(this.dataset.variantId);
      const lineKey = this.dataset.lineKey;
      const wasInCart = this.dataset.inCart === "true";

      if (!variantId || (shouldAdd && wasInCart) || (!shouldAdd && !wasInCart)) return;

      this.setLoading(true);

      try {
        if (shouldAdd) {
          await this.addPackaging(variantId);
        } else {
          await this.removePackaging(lineKey);
        }

        const drawer = document.querySelector("m-cart-drawer");
        const cart = drawer && typeof drawer.onCartDrawerUpdate === "function"
          ? await drawer.onCartDrawerUpdate(true)
          : await this.getCart();

        this.publishCartUpdate(cart);
      } catch (error) {
        event.currentTarget.checked = !shouldAdd;
        this.showError();
        console.error("[ECOMHARD] Erro ao atualizar embalagem:", error);
      } finally {
        this.setLoading(false);
      }
    }

    async addPackaging(variantId) {
      const response = await fetch(this.getRoute("cart_add_url", "cart/add.js"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _ecomhard_embalagem: "true",
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(await this.getResponseError(response));
      }

      return response.json();
    }

    async removePackaging(lineKey) {
      if (!lineKey) {
        throw new Error("Linha da embalagem não encontrada no carrinho.");
      }

      const response = await fetch(this.getRoute("cart_change_url", "cart/change.js"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error(await this.getResponseError(response));
      }

      return response.json();
    }

    getRoute(routeName, fallbackPath) {
      const configuredRoute = window.MinimogSettings?.routes?.[routeName];
      if (configuredRoute) return configuredRoute;

      const root = window.Shopify?.routes?.root || "/";
      return `${root}${fallbackPath}`.replace(/\/\/{2,}/g, "/");
    }

    getCart() {
      const root = window.Shopify?.routes?.root || "/";
      const cartUrl = `${root}cart.js`.replace(/\/\/{2,}/g, "/");
      return fetch(cartUrl, { headers: { Accept: "application/json" } }).then((response) => {
        if (!response.ok) throw new Error("Não foi possível carregar o carrinho.");
        return response.json();
      });
    }

    async getResponseError(response) {
      try {
        const data = await response.json();
        return data.description || data.message || "Não foi possível atualizar o carrinho.";
      } catch (error) {
        return "Não foi possível atualizar o carrinho.";
      }
    }

    publishCartUpdate(cart) {
      if (!cart) return;

      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          ...cart,
          source: "ecomhard-packaging",
        });
      }

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );
    }

    setLoading(loading) {
      this.isLoading = loading;
      this.classList.toggle("is-loading", loading);

      if (this.checkbox) {
        this.checkbox.disabled = loading;
        this.checkbox.setAttribute("aria-busy", loading ? "true" : "false");
      }
    }

    showError() {
      const message = "Não foi possível atualizar a embalagem. Tente novamente.";

      if (window.MinimogTheme?.Notification?.show) {
        window.MinimogTheme.Notification.show({
          target: this,
          type: "warning",
          message,
        });
      }
    }
  }

  customElements.define("ecomhard-packaging-selector", EcomhardPackagingSelector);
}

